import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "@phosphor-icons/react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui-bits";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: pw });
      toast.success("Password updated");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="change-password-page">
      <PageHeader title="Change Password" subtitle="Update your account password" />
      <form onSubmit={submit} className="max-w-md border border-border bg-card p-6 space-y-3">
        <div className="flex items-center gap-2 text-primary mb-2"><Lock size={20} weight="bold" /><span className="font-semibold">Account Security</span></div>
        <div><Label className="text-xs">Current Password</Label><div className="relative"><Input type={showOld ? "text" : "password"} value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="rounded-sm pr-10" data-testid="current-password" /><button type="button" onClick={() => setShowOld((v) => !v)} tabIndex={-1} aria-label={showOld ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="current-password-toggle">{showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
        <div><Label className="text-xs">New Password</Label><div className="relative"><Input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-sm pr-10" data-testid="new-password" /><button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="new-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
        <div><Label className="text-xs">Confirm New Password</Label><div className="relative"><Input type={showPw2 ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} className="rounded-sm pr-10" data-testid="confirm-password" /><button type="button" onClick={() => setShowPw2((v) => !v)} tabIndex={-1} aria-label={showPw2 ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="confirm-password-toggle">{showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
        <p className="text-[11px] text-muted-foreground">Minimum 8 characters, with at least one letter and one number.</p>
        <Button type="submit" disabled={busy} className="rounded-sm" data-testid="change-password-submit">{busy ? "Updating…" : "Update Password"}</Button>
      </form>
    </div>
  );
}
